const React = require('react');
const { createElement: h, useState, useEffect, useRef } = React;

function formatElapsed(ms) {
  var totalSec = Math.max(0, Math.floor(ms / 1000));
  if (totalSec < 60) return totalSec + 's';
  var m = Math.floor(totalSec / 60);
  var s = totalSec % 60;
  return m + 'm ' + (s < 10 ? '0' + s : s) + 's';
}

function useElapsed(startedAt, active) {
  var [now, setNow] = useState(Date.now());
  useEffect(function() {
    if (!active) return;
    var id = setInterval(function() { setNow(Date.now()); }, 1000);
    return function() { clearInterval(id); };
  }, [active, startedAt]);
  return formatElapsed(now - (startedAt || now));
}

function renderIcon(isDone, isCurrent) {
  if (isCurrent) {
    return h('span', { className: 'coe-ask-activity-icon' },
      h('span', { className: 'coe-ask-activity-spinner', 'aria-label': 'loading' })
    );
  }
  var glyph = isDone ? '\u2713' : '\u2022';
  return h('span', { className: 'coe-ask-activity-icon' }, glyph);
}

function isThinkingItem(item) {
  return !!(item && typeof item.text === 'string' && /^thinking/i.test(item.text.trim()));
}

function renderItem(item, idx, inlineThinkText) {
  var isDone = item.status === 'done';
  var isCurrent = item.status === 'current';
  var cls = 'coe-ask-activity-item';
  if (isDone) cls += ' coe-ask-activity-item--done';
  if (isCurrent) cls += ' coe-ask-activity-item--current';
  return h('div', { key: idx, className: cls },
    renderIcon(isDone, isCurrent),
    h('div', { className: 'coe-ask-activity-item-body' },
      h('span', { className: 'coe-ask-activity-text' }, item.text),
      inlineThinkText
        ? h(ThinkingStream, { text: inlineThinkText })
        : null
    )
  );
}

function ThinkingStream(props) {
  var text = props.text || '';
  var bodyRef = useStickyBottomScroll(text);
  if (!text) return null;
  return h('div', { className: 'coe-ask-activity-think-body', ref: bodyRef }, text);
}

function useStickyBottomScroll(text) {
  var ref = useRef(null);
  // Track whether the user has scrolled up to read \u2014 when they have, we stop
  // forcing the view to the bottom until they scroll back down.
  var pinnedRef = useRef(true);

  useEffect(function() {
    var el = ref.current;
    if (!el) return;
    function onScroll() {
      var distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      pinnedRef.current = distanceFromBottom < 16;
    }
    el.addEventListener('scroll', onScroll);
    return function() { el.removeEventListener('scroll', onScroll); };
  }, []);

  useEffect(function() {
    var el = ref.current;
    if (!el) return;
    if (pinnedRef.current) el.scrollTop = el.scrollHeight;
  }, [text]);

  return ref;
}

function ThinkingBlock(props) {
  var text = props.text || '';
  var [expanded, setExpanded] = useState(true);
  var bodyRef = useStickyBottomScroll(text);
  if (!text) return null;
  return h('div', { className: 'coe-ask-activity-think' },
    h('button', {
      type: 'button',
      className: 'coe-ask-activity-think-toggle',
      onClick: function() { setExpanded(function(v) { return !v; }); }
    },
      h('span', { className: 'coe-ask-activity-think-caret' }, expanded ? '\u25be' : '\u25b8'),
      h('span', null, 'Thinking\u2026')
    ),
    expanded
      ? h('div', { className: 'coe-ask-activity-think-body', ref: bodyRef }, text)
      : null
  );
}

function ProbeOutputBlock(props) {
  var probe = props.probe || {};
  var [open, setOpen] = useState(false);
  var hasOutput = !!String(probe.output || '').trim();
  var hasScript = !!String(probe.script || '').trim();
  var label = (probe.label || 'probe') + (probe.probeType ? ' (' + probe.probeType + ')' : '');
  var statusCls = probe.status === 'error' ? ' coe-ask-probe-row--error' : '';
  return h('div', { className: 'coe-ask-probe-row' + statusCls },
    h('button', {
      type: 'button',
      className: 'coe-ask-probe-toggle',
      onClick: function() { setOpen(function(v) { return !v; }); },
      disabled: !hasOutput && !hasScript
    },
      h('span', { className: 'coe-ask-activity-think-caret' }, open ? '\u25be' : '\u25b8'),
      h('span', { className: 'coe-ask-probe-label' }, 'Round ' + (probe.round || 1) + ' \u00b7 ' + label),
      probe.status === 'error' ? h('span', { className: 'coe-ask-probe-status' }, 'error') : null
    ),
    open
      ? h('div', { className: 'coe-ask-probe-body' },
          hasScript
            ? h('div', { className: 'coe-ask-probe-section' },
                h('div', { className: 'coe-ask-probe-section-title' }, 'Script'),
                h('pre', { className: 'coe-ask-probe-pre' }, probe.script)
              )
            : null,
          hasOutput
            ? h('div', { className: 'coe-ask-probe-section' },
                h('div', { className: 'coe-ask-probe-section-title' }, 'Output'),
                h('pre', { className: 'coe-ask-probe-pre' }, probe.output)
              )
            : null
        )
      : null
  );
}

function AskActivityLog(props) {
  var msg = props.msg || {};
  var activity = Array.isArray(msg.activity) ? msg.activity : [];
  var probes = Array.isArray(msg.probes) ? msg.probes : [];
  var streamingText = msg.streamingText || '';
  var streamingThink = msg.streamingThink || '';
  var active = msg.streaming !== false;
  var elapsed = useElapsed(msg.startedAt, active);
  // Final/inspection mode: collapse the whole investigation behind a toggle
  // so the message stays compact but the trace can be reopened later.
  var [collapsed, setCollapsed] = useState(!active);

  // Find the current Thinking item so we can stream the live think text
  // inline beneath its label instead of rendering a separate block below.
  var inlineThinkingIdx = -1;
  if (active && streamingThink) {
    for (var i = activity.length - 1; i >= 0; i -= 1) {
      if (activity[i] && activity[i].status === 'current' && isThinkingItem(activity[i])) {
        inlineThinkingIdx = i;
        break;
      }
    }
  }
  // If the stream emitted think text before any "Thinking" activity item
  // arrived, keep the old standalone block as a fallback so the text is
  // still visible.
  var showStandaloneThink = active && streamingThink && inlineThinkingIdx === -1;

  if (!active && activity.length === 0 && probes.length === 0) return null;

  if (!active) {
    var summary = 'Investigation log';
    if (probes.length > 0) {
      summary += ' \u00b7 ' + probes.length + ' probe' + (probes.length > 1 ? 's' : '');
    } else if (activity.length > 0) {
      summary += ' \u00b7 ' + activity.length + ' step' + (activity.length > 1 ? 's' : '');
    }
    return h('div', { className: 'coe-ask-activity coe-ask-activity--final' },
      h('button', {
        type: 'button',
        className: 'coe-ask-activity-toggle',
        onClick: function() { setCollapsed(function(v) { return !v; }); }
      },
        h('span', { className: 'coe-ask-activity-think-caret' }, collapsed ? '\u25b8' : '\u25be'),
        h('span', null, summary),
        h('span', { className: 'coe-ask-activity-timer coe-ask-activity-timer--final' }, elapsed)
      ),
      collapsed
        ? null
        : h('div', { className: 'coe-ask-activity-final-body' },
            activity.length > 0
              ? h('div', { className: 'coe-ask-activity-list' }, activity.map(function(item, idx) {
                  return renderItem(item, idx, '');
                }))
              : null,
            probes.length > 0
              ? h('div', { className: 'coe-ask-probe-list' }, probes.map(function(p, idx) {
                  return h(ProbeOutputBlock, { key: idx, probe: p });
                }))
              : null
          )
    );
  }

  return h('div', { className: 'coe-ask-activity' },
    h('div', { className: 'coe-ask-activity-header' },
      h('span', { className: 'coe-ask-activity-timer' }, elapsed),
      h('span', { className: 'coe-ask-activity-status' }, 'working\u2026')
    ),
    activity.length > 0
      ? h('div', { className: 'coe-ask-activity-list' }, activity.map(function(item, idx) {
          var inline = idx === inlineThinkingIdx ? streamingThink : '';
          return renderItem(item, idx, inline);
        }))
      : null,
    probes.length > 0
      ? h('div', { className: 'coe-ask-probe-list' }, probes.map(function(p, idx) {
          return h(ProbeOutputBlock, { key: idx, probe: p });
        }))
      : null,
    showStandaloneThink
      ? h(ThinkingBlock, { text: streamingThink })
      : null,
    streamingText
      ? h('div', { className: 'coe-ask-activity-stream' }, streamingText)
      : null
  );
}

module.exports = AskActivityLog;
